import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, DynamoDbTable, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { MetricColumn } from './cloud-metrics-panel.component';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'tables' | 'activity';

const HISTORY_METRICS: MetricColumn[] = [
  { key: 'readThr',   label: 'Read thr'  },
  { key: 'writeThr',  label: 'Write thr' },
  { key: 'sysErr',    label: 'Sys err'   },
  { key: 'throttled', label: 'Throttled' },
];

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['billing', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'      },
  { value: 'name-desc',       label: 'Name Z → A'      },
  { value: 'monitored-first', label: 'Monitored first' },
];

const FACETS: FacetDef[] = [
  {
    key: 'billing',
    label: 'Billing',
    options: [
      { value: 'PROVISIONED',     label: 'Provisioned'  },
      { value: 'PAY_PER_REQUEST', label: 'On-demand'    },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

/**
 * DynamoDB monitoring is at the table level (CloudWatch TableName dimension) —
 * throttling (read/write) and system errors. The monitored resource identifier
 * is the table name. Per-index (GSI) monitoring is not supported in v1.
 */
@Component({
  selector: 'app-dynamodb-detail',
  templateUrl: './dynamodb-detail.component.html',
})
export class DynamoDbDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  tables: DynamoDbTable[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'tables',   label: 'Tables'   },
    { id: 'activity', label: 'Activity' },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'dynamodb', name: 'DynamoDB', icon: 'table', route: '/cloud/dynamodb', status: 'healthy', metrics: [] };
  readonly historyMetrics = HISTORY_METRICS;

  drawerItemId: string | null = null;
  selectedId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('dynamodb').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = params as Record<string, string>;
      this.filters = parseFiltersFromParams(p, DEFAULT_SORT, FACET_KEYS);
      this.selectedId = p['r'] ?? null;
    });
    this.loadData(false);
  }

  /** Loads the table list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getDynamoDbTables(bypass).subscribe(tables => {
      this.tables     = tables;
      this.loading    = false;
      this.refreshing = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'DYNAMODB'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'tables';
    this.selectResource(item.resourceId);
  }

  // ── Overview counts ─────────────────────────────────────────────────────────

  get totalCount():      number { return this.tables.length; }
  get monitoredCount():  number { return this.tables.filter(t => this.isMonitored(t.name)).length; }
  get onDemandCount():   number { return this.tables.filter(t => t.billingMode === 'PAY_PER_REQUEST').length; }
  get provisionedCount(): number { return this.tables.filter(t => t.billingMode === 'PROVISIONED').length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredTables(): DynamoDbTable[] {
    let result = this.tables;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(t => t.name.toLowerCase().includes(q));

    const billing = this.filters.facets['billing'] ?? [];
    if (billing.length) result = result.filter(t => billing.includes(t.billingMode));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(t => {
        const mon = this.isMonitored(t.name);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(tables: DynamoDbTable[], sort: string): DynamoDbTable[] {
    return [...tables].sort((a, b) => {
      switch (sort) {
        case 'name-desc':       return b.name.localeCompare(a.name);
        case 'monitored-first': {
          const aM = this.isMonitored(a.name) ? 0 : 1;
          const bM = this.isMonitored(b.name) ? 0 : 1;
          return aM - bM || a.name.localeCompare(b.name);
        }
        default: return a.name.localeCompare(b.name);
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  billingLabel(mode: string): string {
    if (mode === 'PAY_PER_REQUEST') return 'On-demand';
    if (mode === 'PROVISIONED')     return 'Provisioned';
    return mode;
  }

  formatSize(bytes: number): string {
    if (bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(tableName: string): boolean { return this.cloud.isMonitored('dynamodb', tableName); }

  toggleMonitoring(tableName: string): void {
    const current = this.cloud.isMonitored('dynamodb', tableName);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('dynamodb', tableName, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Metric-history drill-down ───────────────────────────────────────────────

  selectResource(tableName: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: tableName }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(tableName: string): void  { this.drawerItemId = tableName; }
  closeItemSettings(): void                   { this.drawerItemId = null; }

  itemOverrideSummary(tableName: string): string {
    return this.cloud.getItemOverrideSummary('dynamodb', tableName);
  }

  // ── Status badge ────────────────────────────────────────────────────────────

  statusClass(status: string): string {
    if (status === 'ACTIVE')                            return 'bg-emerald-100 text-emerald-700';
    if (status === 'DELETING' || status === 'INACCESSIBLE_ENCRYPTION_CREDENTIALS') return 'bg-red-100 text-red-700';
    if (status === 'UNKNOWN')                           return 'bg-slate-100 text-slate-500';
    return 'bg-amber-100 text-amber-700'; // CREATING / UPDATING — transitional
  }
}
